import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import { Info, Receipt, ShoppingCart, Plus, User, Home, Calendar, DollarSign, FileText, ArrowRightLeft, CalendarPlus } from 'lucide-react';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import Badge from '../shared/Badge';
import Loading from '../shared/Loading';
import ConsumosList from '../consumos/ConsumosList';
import AgregarConsumoModal from '../consumos/AgregarConsumoModal';
import CuentaCorriente from './CuentaCorriente';
import EstadoCuentaSoldado from '../cuentasCobro/EstadoCuentaSoldado';
import { GET_HOSPEDAJE, CAMBIAR_HABITACION_HOSPEDAJE, EXTENDER_ESTANCIA } from '../../graphql/hospedajes';
import { GET_HABITACIONES } from '../../graphql/habitaciones';
import { GET_CONSUMOS_POR_HOSPEDAJE } from '../../graphql/consumos';
import './HospedajeDetalleModal.css';

function HospedajeDetalleModal({ isOpen, onClose, hospedajeId, onCheckOut }) {
  const [pestanaActiva, setPestanaActiva] = useState('informacion'); // 'informacion', 'consumos', 'cuenta', 'cuentas_cobro'
  const [showAgregarConsumo, setShowAgregarConsumo] = useState(false);
  const [estadoCuentaOcupanteId, setEstadoCuentaOcupanteId] = useState(null);
  const [modalCambiarHab, setModalCambiarHab] = useState(false);
  const [nuevaHabitacionId, setNuevaHabitacionId] = useState('');
  const [modalExtender, setModalExtender] = useState(false);
  const [nuevaFechaSalida, setNuevaFechaSalida] = useState('');
  const [obsExtension, setObsExtension] = useState('');

  // Query para obtener datos del hospedaje
  const { data, loading, error, refetch } = useQuery(GET_HOSPEDAJE, {
    variables: { id: parseInt(hospedajeId, 10) },
    skip: !hospedajeId || !isOpen,
    fetchPolicy: 'network-only'
  });

  // Query para obtener consumos
  const { data: dataConsumos, refetch: refetchConsumos } = useQuery(GET_CONSUMOS_POR_HOSPEDAJE, {
    variables: { hospedajeId: parseInt(hospedajeId, 10) },
    skip: !hospedajeId || !isOpen,
    fetchPolicy: 'network-only'
  });

  // Query y mutations para Cambiar Habitación / Extender Estadía
  const { data: habitacionesData } = useQuery(GET_HABITACIONES, {
    variables: { estado: 'disponible' },
    skip: !isOpen
  });
  const [cambiarHabitacion, { loading: cambiando }] = useMutation(CAMBIAR_HABITACION_HOSPEDAJE);
  const [extenderEstancia, { loading: extendiendo }] = useMutation(EXTENDER_ESTANCIA);

  const habitacionesDisponibles = habitacionesData?.habitaciones || [];

  const hospedaje = data?.hospedaje;
  const consumos = dataConsumos?.consumosPorHospedaje || [];

  const handleConsumoAgregado = () => {
    refetchConsumos();
    refetch();
  };

  const handleConsumoEliminado = () => {
    refetchConsumos();
    refetch();
  };

  const handleAbrirCambiarHab = () => {
    setNuevaHabitacionId('');
    setModalCambiarHab(true);
  };

  const handleConfirmarCambioHab = async () => {
    if (!nuevaHabitacionId || !hospedaje) return;
    try {
      await cambiarHabitacion({
        variables: {
          id: parseInt(hospedaje.id),
          nueva_habitacion_id: parseInt(nuevaHabitacionId)
        }
      });
      setModalCambiarHab(false);
      refetch();
    } catch (err) {
      toast.error(err.message || 'Error en la operación');
    }
  };

  const handleAbrirExtender = () => {
    if (!hospedaje) return;
    const fechaActual = new Date(hospedaje.fecha_salida_prevista);
    fechaActual.setDate(fechaActual.getDate() + 1);
    setNuevaFechaSalida(fechaActual.toISOString().split('T')[0]);
    setObsExtension('');
    setModalExtender(true);
  };

  const handleConfirmarExtension = async () => {
    if (!nuevaFechaSalida || !hospedaje) return;
    try {
      await extenderEstancia({
        variables: {
          input: {
            hospedaje_id: parseInt(hospedaje.id),
            nueva_fecha_salida: nuevaFechaSalida,
            observaciones: obsExtension || null
          }
        }
      });
      setModalExtender(false);
      refetch();
    } catch (err) {
      toast.error(err.message || 'Error en la operación');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-CO', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrecio = (precio) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(precio);
  };

  const getEstadoBadgeVariant = (estado) => {
    const variants = {
      activo: 'success',
      finalizado: 'secondary',
      cancelado: 'danger'
    };
    return variants[estado] || 'default';
  };

  const pestanas = [
    { id: 'informacion', label: 'Información', icon: Info },
    { id: 'consumos', label: 'Consumos', icon: ShoppingCart, badge: consumos.length },
    { id: 'cuenta', label: 'Cuenta Corriente', icon: Receipt },
    { id: 'cuentas_cobro', label: 'Cuentas de Cobro', icon: FileText }
  ];

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Cargando..." size="large">
        <Loading />
      </Modal>
    );
  }

  if (error || !hospedaje) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Error" size="large">
        <div className="error-message">
          {error ? error.message : 'No se pudo cargar la información del hospedaje'}
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Hospedaje ${hospedaje.codigo}`}
        size="large"
      >
        <div className="hospedaje-detalle-modal">
          {/* Header con información básica */}
          <div className="hospedaje-header">
            <div className="hospedaje-header__info">
              <div className="hospedaje-header__principal">
                <Home size={20} />
                <span>Habitación {hospedaje.habitacion?.numero} - {hospedaje.habitacion?.tipo}</span>
              </div>
              <div className="hospedaje-header__huesped">
                <User size={16} />
                <span>{hospedaje.huesped?.nombre_completo}</span>
              </div>
            </div>
            <Badge variant={getEstadoBadgeVariant(hospedaje.estado)}>
              {hospedaje.estado}
            </Badge>
          </div>

          {/* Pestañas */}
          <div className="pestanas-container">
            <div className="pestanas-header">
              {pestanas.map((pestana) => {
                const IconComponent = pestana.icon;
                return (
                  <button
                    key={pestana.id}
                    type="button"
                    className={`pestana-btn ${pestanaActiva === pestana.id ? 'pestana-btn--active' : ''}`}
                    onClick={() => setPestanaActiva(pestana.id)}
                  >
                    <IconComponent size={18} />
                    <span>{pestana.label}</span>
                    {pestana.badge !== undefined && pestana.badge > 0 && (
                      <span className="pestana-badge">{pestana.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="pestanas-content">
              {/* Pestaña: Información */}
              {pestanaActiva === 'informacion' && (
                <div className="info-section">
                  <div className="info-grid">
                    <div className="info-card">
                      <div className="info-card__label">
                        <Calendar size={16} />
                        Check-in
                      </div>
                      <div className="info-card__value">
                        {formatFecha(hospedaje.fecha_entrada)}
                      </div>
                    </div>

                    <div className="info-card">
                      <div className="info-card__label">
                        <Calendar size={16} />
                        Check-out Esperado
                      </div>
                      <div className="info-card__value">
                        {formatFecha(hospedaje.fecha_salida_prevista)}
                      </div>
                    </div>

                    <div className="info-card">
                      <div className="info-card__label">
                        <DollarSign size={16} />
                        Precio por Noche
                      </div>
                      <div className="info-card__value info-card__value--price">
                        {formatPrecio(hospedaje.precio_noche)}
                      </div>
                    </div>

                    <div className="info-card">
                      <div className="info-card__label">
                        Noches
                      </div>
                      <div className="info-card__value">
                        {hospedaje.noches_previstas} {hospedaje.noches_previstas === 1 ? 'noche' : 'noches'}
                      </div>
                    </div>
                  </div>

                  {hospedaje.observaciones && (
                    <div className="observaciones-section">
                      <h4>Observaciones</h4>
                      <p>{hospedaje.observaciones}</p>
                    </div>
                  )}

                  {hospedaje.notas_especiales && (
                    <div className="notas-section">
                      <h4>Notas Especiales</h4>
                      <p>{hospedaje.notas_especiales}</p>
                    </div>
                  )}

                  {hospedaje.acompanantes && hospedaje.acompanantes.length > 0 && (
                    <div className="acompanantes-section">
                      <h4>Acompañantes ({hospedaje.acompanantes.length})</h4>
                      <div className="acompanantes-list">
                        {hospedaje.acompanantes.map((acomp, index) => (
                          <div key={index} className="acompanante-detail-item">
                            <User size={16} />
                            <div className="acompanante-detail-info">
                              <span className="acompanante-detail-nombre">
                                {acomp.nombres ? `${acomp.nombres} ${acomp.apellidos || ''}`.trim() : acomp.nombre}
                              </span>
                              {acomp.numero_documento && (
                                <span className="acompanante-detail-doc">
                                  {acomp.tipo_documento || 'Doc'}: {acomp.numero_documento}
                                </span>
                              )}
                              {acomp.parentesco && (
                                <span className="acompanante-detail-extra">
                                  {acomp.parentesco}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Pestaña: Consumos */}
              {pestanaActiva === 'consumos' && (
                <div className="consumos-section">
                  {hospedaje.estado === 'activo' && (
                    <div className="consumos-section__header">
                      <Button
                        variant="primary"
                        onClick={() => setShowAgregarConsumo(true)}
                        icon={<Plus size={18} />}
                      >
                        Agregar Consumo
                      </Button>
                    </div>
                  )}

                  <ConsumosList
                    consumos={consumos}
                    onConsumoEliminado={handleConsumoEliminado}
                    showActions={hospedaje.estado === 'activo'}
                  />
                </div>
              )}

              {/* Pestaña: Cuenta Corriente */}
              {pestanaActiva === 'cuenta' && (
                <div className="cuenta-section">
                  <CuentaCorriente hospedajeId={hospedajeId} />
                </div>
              )}

              {/* Pestaña: Cuentas de Cobro */}
              {pestanaActiva === 'cuentas_cobro' && (
                <div className="cuentas-cobro-section">
                  {hospedaje.ocupantes && hospedaje.ocupantes.length > 0 ? (
                    <div className="ocupantes-cuentas-list">
                      <p style={{ fontSize: '0.85rem', color: 'var(--hotel-text-secondary)', marginBottom: '0.75rem' }}>
                        Seleccione un soldado para ver su estado de cuenta:
                      </p>
                      {hospedaje.ocupantes.map(oc => (
                        <div
                          key={oc.id}
                          className="ocupante-cuenta-item"
                          onClick={() => setEstadoCuentaOcupanteId(oc.id)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.75rem 1rem', border: '1px solid var(--hotel-border)',
                            borderRadius: '8px', cursor: 'pointer', marginBottom: '0.5rem',
                            transition: 'all 0.15s', background: 'var(--hotel-card-background)'
                          }}
                          onMouseOver={e => e.currentTarget.style.background = 'var(--hotel-background)'}
                          onMouseOut={e => e.currentTarget.style.background = 'var(--hotel-card-background)'}
                        >
                          <div>
                            <strong style={{ fontSize: '0.9rem' }}>{oc.nombre}</strong>
                            <div style={{ fontSize: '0.75rem', color: 'var(--hotel-text-secondary)' }}>
                              {oc.grado_militar?.nombre || 'Sin grado'} - Doc: {oc.numero_documento || 'N/A'} - Cama {oc.cama_numero || '-'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                              {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(oc.precio_noche)}/noche
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--hotel-primary)' }}>Ver estado de cuenta →</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--hotel-text-secondary)', textAlign: 'center', padding: '2rem' }}>
                      No hay ocupantes registrados en este hospedaje
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer con acciones */}
          <div className="modal-footer">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
            {hospedaje.estado === 'activo' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleAbrirCambiarHab}
                  icon={<ArrowRightLeft size={16} />}
                >
                  Cambiar Hab.
                </Button>
                <Button
                  variant="outline"
                  onClick={handleAbrirExtender}
                  icon={<CalendarPlus size={16} />}
                >
                  Extender
                </Button>
                {onCheckOut && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      onClose();
                      onCheckOut(hospedaje);
                    }}
                  >
                    Realizar Check-Out
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal para agregar consumo */}
      <AgregarConsumoModal
        isOpen={showAgregarConsumo}
        onClose={() => setShowAgregarConsumo(false)}
        hospedajeId={hospedajeId}
        habitacionId={hospedaje.habitacion?.id}
        onSuccess={handleConsumoAgregado}
      />

      {/* Modal estado de cuenta del soldado */}
      {estadoCuentaOcupanteId && (
        <EstadoCuentaSoldado
          ocupanteId={estadoCuentaOcupanteId}
          onClose={() => setEstadoCuentaOcupanteId(null)}
        />
      )}

      {/* Sub-modal: Cambiar Habitación */}
      {modalCambiarHab && hospedaje && (
        <div className="submodal-overlay" onClick={() => setModalCambiarHab(false)}>
          <div className="submodal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="submodal-title">
              <ArrowRightLeft size={20} /> Cambiar Habitación
            </h3>
            <p className="submodal-info">
              Hospedaje: <strong>{hospedaje.codigo}</strong>
            </p>
            <p className="submodal-info">
              Habitación actual: <strong>#{hospedaje.habitacion?.numero} - {hospedaje.habitacion?.tipo}</strong>
            </p>
            <label className="submodal-label">Nueva Habitación:</label>
            <select
              className="submodal-select"
              value={nuevaHabitacionId}
              onChange={(e) => setNuevaHabitacionId(e.target.value)}
            >
              <option value="">Seleccionar habitación...</option>
              {habitacionesDisponibles.map(hab => (
                <option key={hab.id} value={hab.id}>
                  #{hab.numero} - {hab.tipo} (Piso {hab.piso})
                </option>
              ))}
            </select>
            <div className="submodal-actions">
              <Button variant="outline" onClick={() => setModalCambiarHab(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmarCambioHab}
                disabled={!nuevaHabitacionId || cambiando}
              >
                {cambiando ? 'Moviendo...' : 'Confirmar Cambio'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-modal: Extender Estadía */}
      {modalExtender && hospedaje && (
        <div className="submodal-overlay" onClick={() => setModalExtender(false)}>
          <div className="submodal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="submodal-title">
              <CalendarPlus size={20} /> Extender Estadía
            </h3>
            <p className="submodal-info">
              Hospedaje: <strong>{hospedaje.codigo}</strong>
            </p>
            <p className="submodal-info">
              Salida actual: <strong>{hospedaje.fecha_salida_prevista?.split('T')[0]}</strong>
            </p>
            <label className="submodal-label">Nueva Fecha de Salida:</label>
            <input
              type="date"
              className="submodal-input"
              value={nuevaFechaSalida}
              onChange={(e) => setNuevaFechaSalida(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
            <label className="submodal-label">Observaciones (opcional):</label>
            <input
              type="text"
              className="submodal-input"
              value={obsExtension}
              onChange={(e) => setObsExtension(e.target.value)}
              placeholder="Motivo de la extensión..."
            />
            <div className="submodal-actions">
              <Button variant="outline" onClick={() => setModalExtender(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmarExtension}
                disabled={!nuevaFechaSalida || extendiendo}
              >
                {extendiendo ? 'Extendiendo...' : 'Confirmar Extensión'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HospedajeDetalleModal;
